// Utility functions index - Export all utilities for easy importing
import { useRef, useState, useEffect } from 'react';

// Missing react-use-gesture - let's create a simple polyfill
export const useSwipeable = (handlers, options = {}) => {
  const { onSwipedLeft, onSwipedRight, onSwipedUp, onSwipedDown } = handlers;
  
  return {
    onTouchStart: (e) => {
      const touch = e.touches[0];
      e.target.startX = touch.clientX;
      e.target.startY = touch.clientY;
    },
    
    onTouchEnd: (e) => {
      if (!e.target.startX || !e.target.startY) return;
      
      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - e.target.startX;
      const deltaY = touch.clientY - e.target.startY;
      
      const minSwipeDistance = 50;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      
      if (Math.max(absX, absY) < minSwipeDistance) return;
      
      if (absX > absY) {
        // Horizontal swipe
        if (deltaX > 0 && onSwipedRight) {
          onSwipedRight({ absX, absY });
        } else if (deltaX < 0 && onSwipedLeft) {
          onSwipedLeft({ absX, absY });
        }
      } else {
        // Vertical swipe
        if (deltaY > 0 && onSwipedDown) {
          onSwipedDown({ absX, absY });
        } else if (deltaY < 0 && onSwipedUp) {
          onSwipedUp({ absX, absY });
        }
      }
    }
  };
};

// Missing react-intersection-observer - create simple polyfill
export const useIntersectionObserver = ({ threshold = 0, rootMargin = '0px', onChange }) => {
  const ref = useRef();
  const [entry, setEntry] = useState(null);
  
  useEffect(() => {
    if (!ref.current) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        setEntry(entry);
        if (onChange) {
          onChange(entry.isIntersecting, entry);
        }
      },
      { threshold, rootMargin }
    );
    
    observer.observe(ref.current);
    
    return () => observer.disconnect();
  }, [threshold, rootMargin, onChange]);
  
  return { ref, entry };
};

// Re-export all utilities
export * from './accessibility';
export * from './performance';  
export * from './errorHandling';

// Common utility functions
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const generateUniqueId = (prefix = 'id') => {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const sanitizeFileName = (fileName) => {
  return fileName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
};

export const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const truncateText = (text, maxLength = 100) => {
  if (text.length <= maxLength) return text;
  return text.substr(0, maxLength) + '...';
};